import React from 'react'
import { Alert, Box, Button, Grid, Radio, RadioGroup, Stack, Text } from '@/components/lib'
import { likertStatements, likertValidAnswers } from '@/utils/utils'

// The value of a Likert response is an object keyed by statement key, which
// carries three states we need to keep apart:
//   key absent        -> the statement hasn't been answered
//   key present, null -> the participant chose "not applicable"
//   key present, int  -> the score of the scale point they chose
// A radio group only speaks strings, so "not applicable" travels as this
// sentinel between the group and the value.
const NOT_APPLICABLE = '__na__'

export default function Likert ({ dataType, value, onChange, isDisabled }) {
  const points = dataType?.scalePoints ?? []
  const statements = likertStatements(dataType)
  const allowsNotApplicable = !!dataType?.includeNotApplicable
  // Narrowed to what this field can still accept, so an answer to a statement
  // since removed — or one scored against a scale point whose value has since
  // changed — isn't carried back into the response when a row is edited. It has
  // stopped counting everywhere else, and no row on screen could clear it.
  const answers = likertValidAnswers(dataType, value)

  if (!points.length || !statements.length) {
    return (
      <Alert status='warning'>
        This field has no {!points.length ? 'scale' : 'statements'} set up yet, so there is nothing to answer.
      </Alert>
    )
  }

  const columnCount = points.length + (allowsNotApplicable ? 1 : 0)
  // One column on narrow screens, so each statement reads as its own block with
  // its options listed under it rather than as an unusably cramped grid.
  //
  // The minimums decide how wide the grid has to be before it fits: roughly
  // 9rem for the statement plus 3.5rem per scale point. A 7-point scale with a
  // "not applicable" column therefore needs about 37rem (592px) of content
  // width — inside the poll's 720px page, or the wider app layout, that fits.
  // A longer scale than that falls back to scrolling inside its own box, which
  // the overflow below allows, rather than widening the page.
  const templateColumns = {
    base: '1fr',
    md: `minmax(9rem, 2fr) repeat(${columnCount}, minmax(3.5rem, 1fr))`
  }

  const selectedFor = key => {
    if (!(key in answers)) return ''
    return answers[key] === null ? NOT_APPLICABLE : String(answers[key])
  }

  const select = (key, selected) => {
    const next = { ...answers }
    if (selected === NOT_APPLICABLE) next[key] = null
    else next[key] = parseInt(selected, 10)
    onChange(next)
  }

  // `answers` only ever holds this field's own statements, so clearing them all
  // really does leave nothing behind — which is what makes the field read as
  // unanswered again rather than as an object with nothing in it
  const clearAll = () => onChange({})

  const answeredCount = statements.filter(s => s.key in answers).length

  return (
    <Box>
      <Box overflowX='auto'>
        <Grid templateColumns={templateColumns} display={{ base: 'none', md: 'grid' }} gap={1} px={2} pb={1}>
          <Box />
          {points.map(point => (
            <Text key={point.value} fontSize='xs' fontWeight='bold' color='fg.muted' textAlign='center'>{point.label}</Text>
          ))}
          {allowsNotApplicable && <Text fontSize='xs' fontWeight='bold' color='fg.muted' textAlign='center'>Not applicable</Text>}
        </Grid>

        <Stack gap={0}>
          {statements.map((statement, index) => (
            <RadioGroup
              key={statement.key}
              name={`${dataType?._id ?? 'likert'}-${statement.key}`}
              value={selectedFor(statement.key)}
              onValueChange={e => select(statement.key, e.value)}
              disabled={isDisabled}
              colorPalette='primary'
              display='grid'
              gridTemplateColumns={templateColumns}
              alignItems='center'
              gap={1}
              py={2}
              px={2}
              bg={index % 2 ? 'bg.subtle' : undefined}
            >
              <Text fontSize='sm' py={{ base: 1, md: 0 }}>{statement.label}</Text>
              {points.map(point => (
                <Box key={point.value} display='flex' justifyContent={{ base: 'flex-start', md: 'center' }}>
                  <Radio value={String(point.value)} disabled={isDisabled}>
                    {/* The header carries the labels once the grid is wide enough */}
                    <Text display={{ base: 'inline', md: 'none' }} fontSize='sm'>{point.label}</Text>
                  </Radio>
                </Box>
              ))}
              {allowsNotApplicable &&
                <Box display='flex' justifyContent={{ base: 'flex-start', md: 'center' }}>
                  <Radio value={NOT_APPLICABLE} disabled={isDisabled}>
                    <Text display={{ base: 'inline', md: 'none' }} fontSize='sm'>Not applicable</Text>
                  </Radio>
                </Box>}
            </RadioGroup>
          ))}
        </Stack>
      </Box>

      <Stack direction='row' justifyContent='space-between' alignItems='center' pt={2} px={2}>
        <Text fontSize='xs' color='fg.muted'>{answeredCount} of {statements.length} answered</Text>
        {answeredCount > 0 && !isDisabled &&
          <Button variant='plain' size='xs' onClick={clearAll}>Clear answers</Button>}
      </Stack>
    </Box>
  )
}
